import {
  type OrganizationDraftPickRankedResponsePagedData,
  rdbV1FiltersYearsList,
} from '@on3/api';
import { withAuthentication } from '@on3/ui-lib/src/hocs/withAuthentication';
import { withValidUrl } from '@on3/ui-lib/src/hocs/withValidUrl';
import { proxyApi } from '@on3/ui-lib/utils/api';
import { TeamDraftRankings } from 'components/Db/Draft/TeamDraftRankings/TeamDraftRankings';
import { DatabaseLayout } from 'components/Layouts/DatabaseLayout';
import { PageWrapper } from 'components/Layouts/PageWrapper';
import type { GetServerSideProps } from 'next';
import { useRouter } from 'next/router';
import { type ITeamDraftTotals } from 'pages/nba/draft/[year]/draft-history/team-draft-totals';

export interface ITeamDraftRankingsProps {
  teamRankings: OrganizationDraftPickRankedResponsePagedData;
  title: string;
  years: number[];
}
const NFLTeamDraftTotals = (props: ITeamDraftTotals) => {
  const router = useRouter();
  const { year, 'year-span': span } = router.query;
  const yearSpan = span && +span !== 1 ? `- Last ${span} Years` : '';
  const yearSpanDescription =
    span && year && +span !== 1 ? ` over a ${span} year span.` : '.';

  const description = `A list of the top ${year} college teams in order of NFL draft totals${yearSpanDescription}`;
  const title = `${year} College Football Team Draft Totals ${yearSpan}`;
  const noIndex = props?.teamRankings?.pagination?.count === 0;

  return (
    <PageWrapper
      {...props}
      description={description}
      noIndex={noIndex}
      title={title}
    >
      <DatabaseLayout>
        <TeamDraftRankings
          {...props}
          key={`NFL-${year}-${span}`}
          title={title}
        />
      </DatabaseLayout>
    </PageWrapper>
  );
};

export const getServerSideProps: GetServerSideProps = async (ctx) => {
  const siteData = await withValidUrl.isValid(ctx);
  const userData = await withAuthentication.isAuth(ctx);

  if (!siteData) {
    return {
      notFound: true,
    };
  }

  const { year, 'year-span': span } = ctx.query;
  const yearSpan = span ? +span : 1;

  const params = {
    sportKey: 1,
    year,
    yearSpan,
  };

  if (!year) {
    return {
      notFound: true,
    };
  }

  try {
    const [teamRankings, years] = await Promise.allSettled([
      proxyApi
        .get(`/public/rdb/v1/draft-pick-organization-rank`, {
          params,
        })
        .then((r) => r.data),
      rdbV1FiltersYearsList(proxyApi),
    ]);

    const data = {
      pageType: 'database',
      siteData: siteData || [],
      userData: userData || {},
      years: years.status === 'fulfilled' ? years.value : [],
      teamRankings:
        teamRankings.status === 'fulfilled' ? teamRankings.value : [],
    };

    return { props: { ...data } };
  } catch (e) {
    console.log(e);

    return { props: [] };
  }
};

export default withValidUrl(NFLTeamDraftTotals);
